import type { RotaWeek } from "@dentacore/shared-types";

/**
 * **One week per person across every location shown** (OQ-D17).
 *
 * Each location answers with its own week for a person; someone working
 * Mon–Wed at one and Thu–Fri at another had only the first location's days
 * drawn. Here each day is taken from the location that has them in (a day off
 * at one never hides a working day at another), tagged with that location's
 * name, and the paid hours and changes are added up. `weeksByLocation` is in
 * the picker's order, so where two locations both have the same day the first
 * wins — the order every other merged screen reads.
 */
export function mergeWeeks(
  weeksByLocation: readonly { name: string | undefined; weeks: readonly RotaWeek[] }[],
): RotaWeek[] {
  const weekOf = new Map<string, RotaWeek>();
  for (const { name, weeks } of weeksByLocation) {
    for (const week of weeks) {
      const tagged: RotaWeek = {
        ...week,
        days: week.days.map((day) =>
          name === undefined || day.kind === "OFF" ? day : { ...day, locationName: name },
        ),
      };
      const had = weekOf.get(week.userId);
      if (had === undefined) {
        weekOf.set(week.userId, tagged);
        continue;
      }
      const days =
        had.days.length === 0
          ? tagged.days
          : had.days.map((day, i) => {
              const there = tagged.days[i];
              return day.kind === "OFF" && there !== undefined && there.kind !== "OFF"
                ? there
                : day;
            });
      const paidMinutes = had.paidMinutes + week.paidMinutes;
      const contractedMinutes = had.contractedMinutes ?? week.contractedMinutes;
      weekOf.set(week.userId, {
        ...had,
        days,
        paidMinutes,
        contractedMinutes,
        differenceMinutes: contractedMinutes === null ? null : paidMinutes - contractedMinutes,
        exceptionCount: had.exceptionCount + week.exceptionCount,
      });
    }
  }
  return [...weekOf.values()];
}
